export const DATA_REGISTRY_PAGE_SIZE = 25;

export const DATA_REGISTRY_VIEW_PERMISSIONS = Object.freeze([
  'administrator',
  'data_registry_view',
  'data_registry_manage',
  'data_registry_sensitive_view',
]);

export const DATA_REGISTRY_MANAGE_PERMISSIONS = Object.freeze([
  'administrator',
  'data_registry_manage',
]);

export const DATA_REGISTRY_SENSITIVE_PERMISSIONS = Object.freeze([
  'administrator',
  'data_registry_sensitive_view',
]);

export const DATA_REGISTRY_TABS = Object.freeze([
  { id: 'sources', label: 'Fontes', icon: 'i-lucide-database' },
  { id: 'runs', label: 'Runs', icon: 'i-lucide-activity' },
  { id: 'people', label: 'Pessoas', icon: 'i-lucide-users' },
  { id: 'records', label: 'Registros', icon: 'i-lucide-list-tree' },
  { id: 'operations', label: 'Operações', icon: 'i-lucide-wrench' },
]);

export const RESTRICTED_VALUE = '[restrito]';
export const RESTRICTED_ERROR = '[erro restrito]';

const staticOptions = values =>
  values.map(value => ({ value, label: value.replace(/_/g, ' ') }));

// Declarative filter surface: each field knows the tabs it belongs to and
// where its options come from — a fixed enum or a facet collected from the
// rows already loaded (no extra round-trip against 500M-row tables).
export const DATA_REGISTRY_FILTER_FIELDS = Object.freeze([
  {
    key: 'sourceId',
    label: 'Fonte',
    emptyLabel: 'Todas',
    tabs: ['sources', 'runs', 'people', 'records'],
  },
  {
    key: 'status',
    label: 'Status',
    tabs: ['sources', 'runs', 'records'],
    options: [
      'active',
      'disabled',
      'queued',
      'processing',
      'processed',
      'processed_partial',
      'failed',
      'cancelled',
    ],
    facet: 'status',
  },
  { key: 'category', label: 'Categoria', tabs: ['sources'], facet: 'category' },
  { key: 'mode', label: 'Modo', tabs: ['runs'], facet: 'mode' },
  { key: 'recordKind', label: 'Tipo', tabs: ['records'], facet: 'recordKind' },
  {
    key: 'subjectType',
    label: 'Assunto',
    tabs: ['records'],
    facet: 'subjectType',
  },
  {
    key: 'riskLevel',
    label: 'Risco',
    tabs: ['records'],
    options: ['unknown', 'low', 'medium', 'high', 'restricted'],
    facet: 'riskLevel',
  },
  {
    key: 'structuredRecordType',
    label: 'Tipo estruturado',
    tabs: ['records'],
    facet: 'structuredRecordType',
  },
]);

// "What the record carries" filters. The backend echoes the authoritative key
// list in `meta.contains_fields`; this map only adds presentation.
export const CONTAINS_FIELDS = Object.freeze([
  { key: 'cpf', label: 'CPF', icon: 'i-lucide-id-card' },
  { key: 'cnpj', label: 'CNPJ', icon: 'i-lucide-building-2' },
  { key: 'nome', label: 'Nome', icon: 'i-lucide-user' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'telefone', label: 'Telefone', icon: 'i-lucide-phone' },
  { key: 'matricula', label: 'Matrícula', icon: 'i-lucide-hash' },
  { key: 'rg', label: 'RG', icon: 'i-lucide-contact' },
  { key: 'endereco', label: 'Endereço', icon: 'i-lucide-map-pin' },
  { key: 'nascimento', label: 'Nascimento', icon: 'i-lucide-calendar' },
  { key: 'cargo', label: 'Cargo', icon: 'i-lucide-briefcase' },
  { key: 'lotacao', label: 'Lotação', icon: 'i-lucide-landmark' },
  { key: 'remuneracao', label: 'Remuneração', icon: 'i-lucide-banknote' },
]);

export const QUICK_CONTAINS_KEYS = Object.freeze([
  'cpf',
  'email',
  'telefone',
  'matricula',
]);

export const containsFieldsFromMeta = (meta = {}) => {
  const allowed = meta?.contains_fields || meta?.containsFields;
  if (!Array.isArray(allowed) || !allowed.length) return CONTAINS_FIELDS;
  const known = new Map(CONTAINS_FIELDS.map(field => [field.key, field]));
  return allowed.map(
    key =>
      known.get(key) || {
        key,
        label: key.replace(/_/g, ' '),
        icon: 'i-lucide-tag',
      }
  );
};

export const optionsForField = (field, facets = {}) => {
  const fromFacet = field?.facet ? facets[field.facet] || [] : [];
  const merged = [...new Set([...(field?.options || []), ...fromFacet])].filter(
    Boolean
  );
  return staticOptions(merged.sort((a, b) => a.localeCompare(b)));
};

// Facets are read off the rows currently loaded, so the selects only ever
// offer values the operator can actually reach.
export const collectFacets = (rows = [], keys = []) =>
  keys.reduce((accumulator, key) => {
    const values = rows
      .map(row => row?.[key])
      .filter(value => typeof value === 'string' && value.trim());
    return { ...accumulator, [key]: [...new Set(values)] };
  }, {});

const pick = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const numberValue = value => Number(value || 0);

export const formatCount = value =>
  new Intl.NumberFormat('pt-BR').format(numberValue(value));

export const formatDateTime = value => {
  if (!value) return 'Sem data';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const normalizeMeta = meta => ({
  count: numberValue(pick(meta?.count, meta?.total_count, meta?.totalCount)),
  currentPage: numberValue(pick(meta?.current_page, meta?.currentPage, 1)) || 1,
  pageSize:
    numberValue(
      pick(meta?.page_size, meta?.pageSize, DATA_REGISTRY_PAGE_SIZE)
    ) || DATA_REGISTRY_PAGE_SIZE,
  canViewUnminified: Boolean(
    pick(meta?.can_view_unminified, meta?.canViewUnminified, false)
  ),
  // The API counts up to a cap on the huge registry tables; these flags keep
  // the console honest about what the number means and whether paging on.
  countCapped: Boolean(pick(meta?.count_capped, meta?.countCapped, false)),
  hasNextPage: Boolean(pick(meta?.has_next_page, meta?.hasNextPage, false)),
  timedOut: Boolean(pick(meta?.timed_out, meta?.timedOut, false)),
  estimatedTableTotal: numberValue(
    pick(meta?.estimated_table_total, meta?.estimatedTableTotal, 0)
  ),
  containsFields: containsFieldsFromMeta(meta),
  // A content filter is paginated by peeking one row ahead instead of counting
  // a terabyte-scale table, so the total is deliberately unknown.
  countUnknown: Boolean(pick(meta?.count_unknown, meta?.countUnknown, false)),
  requiresScope: Boolean(
    pick(meta?.requires_scope, meta?.requiresScope, false)
  ),
});

export const formatMetaCount = meta => {
  const total = formatCount(meta?.count);
  if (meta?.countUnknown) return meta?.hasNextPage ? `${total}+` : total;
  return meta?.countCapped ? `${total}+` : total;
};

export const normalizePageResponse = (response, mapper = item => item) => ({
  items: Array.isArray(response?.payload) ? response.payload.map(mapper) : [],
  meta: normalizeMeta(response?.meta || {}),
});

export const registryStatusTone = status => {
  const normalized = String(status || '').toLowerCase();
  if (['processed', 'active', 'complete', 'healthy'].includes(normalized)) {
    return 'positive';
  }
  if (['processing', 'running', 'queued'].includes(normalized)) return 'info';
  if (['failed', 'error', 'restricted'].includes(normalized)) return 'danger';
  if (
    ['disabled', 'cancelled', 'stale', 'processed_partial'].includes(normalized)
  ) {
    return 'warning';
  }
  return 'neutral';
};

export const riskTone = risk => {
  const normalized = String(risk || '').toLowerCase();
  if (['high', 'restricted'].includes(normalized)) return 'danger';
  if (normalized === 'medium') return 'warning';
  if (normalized === 'low') return 'positive';
  return 'neutral';
};

export const normalizeSource = source => ({
  id: source?.id || '',
  key: source?.key || '',
  name: source?.name || '',
  category: source?.category || '',
  status: source?.status || 'unknown',
  adapterKey: source?.adapter_key || source?.adapterKey || '',
  sensitivityLevel: source?.sensitivity_level || source?.sensitivityLevel || '',
  sourceUrl: source?.source_url || source?.sourceUrl || '',
  sourceUrls: Array.isArray(source?.source_urls)
    ? source.source_urls
    : source?.sourceUrls || [],
  lastListedAt: source?.last_listed_at || source?.lastListedAt || '',
  updatedAt: source?.updated_at || source?.updatedAt || '',
});

export const normalizeRun = run => {
  const listedCount = numberValue(run?.listed_count || run?.listedCount);
  const storedCount = numberValue(run?.stored_count || run?.storedCount);
  const failedCount = numberValue(run?.failed_count || run?.failedCount);
  const status = run?.status || 'unknown';
  const hasError = Boolean(
    run?.error_message || run?.errorMessage || failedCount
  );
  let lockLabel = 'sem lock ativo';
  if (status === 'processing') {
    lockLabel = 'lock ativo';
  } else if (hasError) {
    lockLabel = 'erro registrado';
  }

  return {
    id: run?.id || '',
    sourceId: run?.source_id || run?.sourceId || '',
    sourceKey: run?.source_key || run?.sourceKey || '',
    mode: run?.mode || '',
    status,
    listedCount,
    storedCount,
    failedCount,
    errorMessage: run?.error_message || run?.errorMessage || '',
    startedAt: run?.started_at || run?.startedAt || '',
    finishedAt: run?.finished_at || run?.finishedAt || '',
    updatedAt: run?.updated_at || run?.updatedAt || '',
    locked: status === 'processing',
    hasError,
    lockLabel,
  };
};

export const normalizePerson = person => {
  const personalDataUnminified =
    person?.personal_data_unminified || person?.personalDataUnminified || null;

  return {
    id: person?.id || '',
    personKey: person?.person_key || person?.personKey || '',
    name: personalDataUnminified?.nome || person?.name || '',
    cpfMinified: person?.cpf_minified || person?.cpfMinified || '',
    cpfUnminified: personalDataUnminified?.cpf || '',
    primaryMatricula:
      person?.primary_matricula || person?.primaryMatricula || '',
    matriculaUnminified: personalDataUnminified?.matricula || '',
    recordsCount: numberValue(person?.records_count || person?.recordsCount),
    personalDataMinified:
      person?.personal_data_minified || person?.personalDataMinified || {},
    personalDataUnminified,
    metadata: person?.metadata || {},
    updatedAt: person?.updated_at || person?.updatedAt || '',
    raw: person || {},
  };
};

export const normalizeRecord = record => {
  const personalDataUnminified =
    record?.personal_data_unminified || record?.personalDataUnminified || null;

  return {
    id: record?.id || '',
    sourceId: record?.source_id || record?.sourceId || '',
    sourceKey: record?.source_key || record?.sourceKey || '',
    sourceName: record?.source_name || record?.sourceName || '',
    personId: record?.person_id || record?.personId || '',
    runId: record?.run_id || record?.runId || '',
    title: record?.title || '',
    summary: record?.summary || '',
    subjectKey: record?.subject_key || record?.subjectKey || '',
    dedupeKey: record?.dedupe_key || record?.dedupeKey || '',
    recordKind: record?.record_kind || record?.recordKind || '',
    structuredRecordType:
      record?.structured_record_type || record?.structuredRecordType || '',
    subjectType: record?.subject_type || record?.subjectType || '',
    status: record?.status || 'unknown',
    riskLevel: record?.risk_level || record?.riskLevel || 'unknown',
    sourceRecordUrl: record?.source_record_url || record?.sourceRecordUrl || '',
    personalDataMinified:
      record?.personal_data_minified || record?.personalDataMinified || {},
    personalDataUnminified,
    data: record?.data || {},
    metadata: record?.metadata || {},
    updatedAt: record?.updated_at || record?.updatedAt || '',
    raw: record || {},
  };
};

const plannedStructuredCount = planned =>
  Object.values(planned?.structured_records || planned?.structuredRecords || {})
    .map(numberValue)
    .reduce((sum, value) => sum + value, 0);

export const normalizeIntegrity = payload => {
  const audit =
    payload?.audit || payload?.after || payload?.before || payload || {};
  const runs = audit?.runs || {};
  const personLinks = audit?.person_links || audit?.personLinks || {};
  const structuredRecords =
    audit?.structured_records || audit?.structuredRecords || {};
  const coverageSummary =
    audit?.coverage?.summary ||
    audit?.coverage_summary ||
    audit?.coverageSummary ||
    {};
  const plannedChanges =
    payload?.planned_changes || payload?.plannedChanges || {};

  return {
    mode: payload?.mode || 'audit',
    healthy: Boolean(audit?.healthy),
    issueCount: numberValue(audit?.issue_count || audit?.issueCount),
    auditedAt: audit?.audited_at || audit?.auditedAt || '',
    changesApplied: Boolean(
      payload?.changes_applied || payload?.changesApplied
    ),
    plannedChanges,
    plannedTotal:
      numberValue(plannedChanges?.stale_runs || plannedChanges?.staleRuns) +
      numberValue(plannedChanges?.person_links || plannedChanges?.personLinks) +
      plannedStructuredCount(plannedChanges),
    runs: {
      processingCount: numberValue(
        runs?.processing_count || runs?.processingCount
      ),
      staleCount: numberValue(runs?.stale_count || runs?.staleCount),
      failedCount: numberValue(runs?.failed_count || runs?.failedCount),
      staleRunIds: runs?.stale_run_ids || runs?.staleRunIds || [],
      failedRunIds: runs?.failed_run_ids || runs?.failedRunIds || [],
    },
    personLinks: {
      missingCount: numberValue(
        personLinks?.missing_count || personLinks?.missingCount
      ),
      repairableCount: numberValue(
        personLinks?.repairable_count || personLinks?.repairableCount
      ),
      mismatchedCount: numberValue(
        personLinks?.mismatched_count || personLinks?.mismatchedCount
      ),
    },
    structuredRecords,
    coverageFailuresCount: numberValue(
      coverageSummary?.coverage_failures_count ||
        coverageSummary?.coverageFailuresCount
    ),
    raw: payload || {},
  };
};

export const parseJsonPayload = (value, fallback) => {
  if (!String(value || '').trim()) return fallback;
  return JSON.parse(value);
};

export const buildUpsertPayload = ({ sourceKey, sourceName, record }) => ({
  source_key: sourceKey,
  source: { key: sourceKey, name: sourceName || sourceKey },
  record,
});

export const buildBulkPayload = ({ sourceKey, sourceName, records }) => ({
  source_key: sourceKey,
  source: { key: sourceKey, name: sourceName || sourceKey },
  records,
});
